import { OrderStatus } from "enum/OrderStatus";
import { Order } from "models/Order";
import React, { useEffect, useMemo, useState } from "react";
import { displayOrderCity } from "helpers/orderAddress";
import { OrderService } from "services/OrderService";
import { Row, Col, CardBody, Card } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { format } from "date-fns";
import { AuthData, useAuth } from "context/AuthProvider";
import { useDispachCount } from "context/OrderCountProvider";
import { AdminService } from "services/AdminService";
import { Puff } from "react-loader-spinner";
import { DispatchType } from "enum/DispatchType";
import ReactPaginate from "react-paginate";

const ReturnOrders: React.FC = () => {
  const [returnOrders, setReturnOrders] = useState<Order[]>([]);
  const [user] = useAuth() as AuthData;
  const [isloading, setIsloading] = useState<boolean>(true);


  const [LIMIT_P_C, setLIMIT_P_C] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [dispachCount, setDispachCount] = useDispachCount();


  const data = {
    status: OrderStatus.RETURN,
    uniqueId: "",
    dispatchType: DispatchType.RETURN_ORDERS,
  };

  const statuscount = async () => {
    try {
      const res = await AdminService.getalldispachcount();
      if (res?.success) {
        setDispachCount(res.data);

        const count = res.data?.returnOrders || 0;
        setTotalCount(count);
        const calculatedPageCount = Math.max(1, Math.ceil(count / LIMIT_P_C));
        setPageCount(calculatedPageCount);
      }
    } catch (err) {
      console.error("Failed to fetch dispatch count:", err);
    }
  };

  const fetchOrders = async (limit = LIMIT_P_C, page = currentPage) => {
    setIsloading(true);
    try {
      const res = await OrderService.getOrdersByStatus(data, limit, page);
      if (res?.success) {
        setReturnOrders(res.data || []);
      } else {
        setReturnOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setReturnOrders([]);
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    statuscount();
    fetchOrders();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [LIMIT_P_C, currentPage]);

  const handlePageChange = (page: { selected: number }) => {
    setCurrentPage(page.selected + 1);
  };

  const handleColumnChange = (selectedOption: any) => {
    setLIMIT_P_C(selectedOption.value);
    setCurrentPage(1);
  };

  const handleSearch = async (searchText: string) => {
    const searchdata = {
      status: OrderStatus.RETURN,
      uniqueId: "",
      searchableString: searchText,
    };

    const res = await OrderService.getOrdersSearchBystatus(searchdata, LIMIT_P_C);
    if (res?.success) {
      const orders = res.data || [];
      setReturnOrders(orders);
      setTotalCount(orders.length);
      const calculatedPageCount = Math.max(1, Math.ceil(orders.length / LIMIT_P_C));
      setPageCount(calculatedPageCount);
      setCurrentPage(1);
    } else {
      setReturnOrders([]);
      setTotalCount(0);
      setPageCount(1);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Order Id",
        accessor: "uniqueId",
        Cell: ({ value }: any) => <div className="link-primary">{value}</div>,
      },
      {
        Header: (
          <div style={{ whiteSpace: "pre-line", width: "150px" }}>
            Individual / Company Name
          </div>
        ),
        accessor: user?.isABusiness
          ? "userId.address.companyName"
          : "userId.address.contactName",
      },
      {
        Header: "Date",
        accessor: "deliveryTime",
        Cell: ({ value }: any) => format(new Date(value), "dd.MM.yyyy"),
      },
      {
        Header: "Pickup",
        accessor: "pickUpAddress.city",
        Cell: ({ row }: any) => displayOrderCity(row.original.pickUpAddress),
      },
      {
        Header: "PPC",
        accessor: "pickUpAddress.postalCode",
        Cell: ({ value }) => <div style={{ width: 80 }}>{value || ""}</div>,
      },
      {
        Header: "Delivery",
        accessor: "deliveryAddress.city",
        Cell: ({ row }: any) => displayOrderCity(row.original.deliveryAddress),
      },
      {
        Header: "DPC",
        accessor: "deliveryAddress.postalCode",
        Cell: ({ value }) => <div style={{ width: 80 }}>{value || ""}</div>,
      },
      {
        Header: "Driver",
        accessor: "driverId.firstName",
        Cell: ({ value }: any) => value || "N/A"
      },
      {
        Header: "Weight (lbs)",
        accessor: "totalWeight",
        Cell: ({ value }: any) => {
          if (!value) return "N/A";
          return String(value).replace(/ pounds/gi, "");
        },
      },
      {
        Header: "Vehicle Type",
        accessor: "vehicleType",
        Cell: ({ value }: any) => value || "N/A"
      },
      {
        Header: "Price",
        accessor: user?.isABusiness ? "subTotal" : "totalFare",
        Cell: ({ value }: any) => {
          if (value === null || value === undefined) return "$0.00";
          return `$${Number(value).toFixed(2)}`;
        },
      },
      {
        Header: "Order Time",
        accessor: "packageReadyTime",
        Cell: ({ row }: any) => {
          const readyTime = row.original.packageReadyTime;
          const deliveryTime = row.original.deliveryTime;

          if (!readyTime || !deliveryTime) return "N/A";

          return `${format(new Date(readyTime), "hh:mma")} to ${format(
            new Date(deliveryTime),
            "hh:mma"
          )}`;
        },
      },
      {
        Header: "Status",
        accessor: "orderStatus",
        Cell: ({ value }: any) => {
          if (!value) return "N/A";
          return String(value)
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char: string) => char.toUpperCase());
        },
      },
    ],
    [user]
  );


  const CustomPagination = () => {
    return (
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-3">
        <div className="text-muted small">
          Showing{" "}
          <span className="fw-semibold">
            {totalCount === 0 ? 0 : (currentPage - 1) * LIMIT_P_C + 1}
          </span>{" "}
          to{" "}
          <span className="fw-semibold">
            {Math.min(currentPage * LIMIT_P_C, totalCount)}
          </span>{" "}
          of <span className="fw-semibold">{totalCount}</span> entries
        </div>

        <ReactPaginate
          previousLabel={"← Previous"}
          nextLabel={"Next →"}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={handlePageChange}
          forcePage={currentPage - 1}
          containerClassName={"pagination justify-content-end align-items-end rounded-pill px-3"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          breakClassName={"page-item"}
          breakLinkClassName={"page-link"}
          activeClassName={"active"}
          disabledClassName={"disabled"}
        />
      </div>
    );
  };

  document.title = "Return Orders | Zulu Courier";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Order Status" breadcrumbItem="View Return Orders" />
        <Col lg={12}>
          <Card>
            <CardBody>
              {isloading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ height: "500px" }}
                >
                  <Puff color="#FFD0B8" height={50} width={50} />
                </div>
              ) : returnOrders.length > 0 ? (
                <>
                  <TableContainer
                    columns={columns}
                    data={returnOrders}
                    handleColumnChange={handleColumnChange}
                    selectedColumns={LIMIT_P_C}
                    showColumnChange={true}
                    handleSearch={handleSearch}
                    isGlobalFilter={true}
                    customPageSize={LIMIT_P_C}
                    className="custom-header-css"
                    isPDFGenerate={true}
                    ispopup={true}
                    isAddressBook={false}
                    hasCheckBox={true} currentPage={undefined} pagelimit={undefined} handlePageChange={undefined} isJobListGlobalFilter={undefined} isAddOptions={undefined} isAddUserList={undefined} handleOrderClicks={undefined} handleUserClick={undefined} handleCustomerClick={undefined} isAddCustList={undefined} hancleDeleteRecords={undefined} handleSelect={undefined} selectedID={undefined} handleSendEmail={undefined} footerData={undefined} />
                  <CustomPagination />
                </>
              ) : (
                <Row className="justify-content-md-center align-items-center">
                  <Col className="col-md-auto">
                    <div>No Return Orders</div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </div>
    </div>
  );
};

export default ReturnOrders;